import { PASSWORD_MIN_LENGTH, registerSchema } from '@cartly/shared';
import { Alert, Button, TextField } from '@cartly/ui';
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api } from '../../api/client';
import { useSubmit } from '../../app/hooks';
import { useSession } from '../../app/session';
import { AuthLayout } from '../../layouts/AuthLayout';

export function RegisterPage() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const { pending, error, fieldErrors, run, setFieldErrors } = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Same schema the API uses, for instant feedback; the server remains authoritative.
    const parsed = registerSchema.safeParse(form);
    if (!parsed.success) {
      const fields: Record<string, string> = {};
      for (const issue of parsed.error.issues) fields[String(issue.path[0])] ??= issue.message;
      setFieldErrors(fields);
      return;
    }
    const created = await run(() => api.post<{ user: unknown }>('/auth/register', parsed.data));
    if (!created) return;
    const state = await refresh();
    if (state.status === 'active') navigate(from ?? '/onboarding', { replace: true });
  };

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start automating customer engagement for your store."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form className="stack" onSubmit={onSubmit} noValidate>
        {error && <Alert tone="critical">{error}</Alert>}
        <TextField
          label="Full name"
          autoComplete="name"
          required
          value={form.name}
          onChange={set('name')}
          error={fieldErrors['name']}
        />
        <TextField
          label="Work email"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={set('email')}
          error={fieldErrors['email']}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LENGTH}
          value={form.password}
          onChange={set('password')}
          hint={`At least ${PASSWORD_MIN_LENGTH} characters. A short sentence works well.`}
          error={fieldErrors['password']}
        />
        <Button type="submit" variant="primary" block loading={pending}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
